import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Search,
  RefreshCw,
  BarChart3,
  AlertTriangle,
  Eye,
  ArrowUpDown,
} from "lucide-react";

interface MemberData {
  summary: {
    totalMembers: number;
    active: number;
    inactive: number;
    pending: number;
  };
  members: Array<{
    id: string;
    memberNumber: number;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    gender: string;
    status: string;
    totalSavings: number;
    totalLoans: number;
    joinedAt: string;
    groupName?: string;
  }>;
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    blue: "bg-blue-50",
    red: "bg-red-50",
    amber: "bg-amber-50",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    blue: "text-blue-600",
    red: "text-red-600",
    amber: "text-amber-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function formatMemberNumber(num: number): string {
  return String(num).padStart(4, "0");
}

const statusColors: Record<string, string> = {
  active: "bg-[#009A44]/10 text-[#009A44]",
  inactive: "bg-gray-100 text-gray-600",
  pending: "bg-amber-50 text-amber-600",
};

export default function MemberReport() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [search, setSearch] = useState("");

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-members", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.groupId) params.groupId = filters.groupId;

      const { data } = await api.get("/reports/members", { params });
      return data?.data as MemberData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || { totalMembers: 0, active: 0, inactive: 0, pending: 0 };
  const allMembers = rawData?.members || [];

  const filteredMembers = allMembers.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      m.firstName.toLowerCase().includes(q) ||
      m.lastName.toLowerCase().includes(q) ||
      m.phone.includes(q) ||
      m.email.toLowerCase().includes(q)
    );
  });

  const exportColumns = [
    { key: "memberNumberDisplay", label: "ID" },
    { key: "fullName", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "gender", label: "Gender" },
    { key: "status", label: "Status" },
    { key: "totalSavings", label: "Savings" },
    { key: "totalLoans", label: "Loans" },
    { key: "joinedAt", label: "Joined" },
  ];

  const exportData = filteredMembers.map((m) => ({
    ...m,
    memberNumberDisplay: formatMemberNumber(m.memberNumber),
    fullName: `${m.firstName} ${m.lastName}`,
    joinedAt: new Date(m.joinedAt).toLocaleDateString(),
  }));

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Member Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Complete member directory and statistics
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load member report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Member Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Complete member directory and statistics
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["status", "groupId"]}
        />

        {/* Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Total Members"
            value={summary.totalMembers}
            icon={<Users className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Active"
            value={summary.active}
            icon={<UserCheck className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Inactive"
            value={summary.inactive}
            icon={<UserX className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Pending"
            value={summary.pending}
            icon={<Clock className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Members Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold">Members Directory</h3>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name, phone, email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 border-gray-200 text-sm w-full sm:w-64"
                  />
                </div>
                <ReportExport
                  data={exportData}
                  filename="member-report"
                  title="Member Report"
                  columns={exportColumns}
                />
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">
                  {search ? "No members match your search" : "No members found"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">ID</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Name</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Phone</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Email</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Gender</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Status</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Savings</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Loans</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Joined</th>
                      <th className="pb-2 font-medium text-gray-500 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => (
                      <tr
                        key={member.id}
                        className="border-b last:border-b-0 hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => navigate(`/reports/member/${member.id}/statement`)}
                      >
                        <td className="py-3 pr-4 font-mono text-xs text-gray-500">
                          {formatMemberNumber(member.memberNumber)}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">{member.phone}</td>
                        <td className="py-3 pr-4 text-gray-600">{member.email}</td>
                        <td className="py-3 pr-4 text-gray-600">{member.gender}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              statusColors[member.status] || "bg-gray-100 text-gray-600"
                            )}
                          >
                            {member.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-[#009A44]">
                          {formatCurrency(member.totalSavings)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-blue-600">
                          {formatCurrency(member.totalLoans)}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {new Date(member.joinedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/reports/member/${member.id}/statement`);
                            }}
                            className="inline-flex items-center gap-1 rounded-md bg-[#009A44]/10 px-2 py-1 text-xs font-medium text-[#009A44] hover:bg-[#009A44]/20"
                          >
                            <Eye className="h-3 w-3" />
                            Statement
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
